import { useState, useEffect } from 'react';
import { differenceInDays, format } from 'date-fns';

interface DailyForecast {
  date: string;
  tempMin: number;
  tempMax: number;
  weatherCode: number;
  precipitationProbability: number;
  windSpeed: number;
}

interface UseWeatherForecastResult {
  forecast: DailyForecast[];
  loading: boolean;
  error: string | null;
  isAvailable: boolean;
  locationName: string;
}

// Rhode-Saint-Genèse, Belgique
const DEFAULT_LATITUDE = 50.7667;
const DEFAULT_LONGITUDE = 4.3833;
const DEFAULT_LOCATION = 'Rhode-Saint-Genèse';

export const useWeatherForecast = (
  dateDebut?: Date,
  dateFin?: Date
): UseWeatherForecastResult => {
  const [forecast, setForecast] = useState<DailyForecast[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coords, setCoords] = useState({ lat: DEFAULT_LATITUDE, lon: DEFAULT_LONGITUDE });
  const [locationName, setLocationName] = useState(DEFAULT_LOCATION);

  const isAvailable = !!(
    dateDebut &&
    dateFin &&
    differenceInDays(dateDebut, new Date()) <= 14 &&
    differenceInDays(dateDebut, new Date()) >= 0
  );

  // Demander la géolocalisation du navigateur
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          setCoords({ lat: latitude, lon: longitude });
          // Reverse geocode pour le nom
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=fr`
            );
            const data = await res.json();
            const city =
              data.address?.city ||
              data.address?.town ||
              data.address?.village ||
              data.address?.municipality ||
              DEFAULT_LOCATION;
            setLocationName(city);
          } catch {
            // keep default
          }
        },
        () => {
          // Silently fall back to default
        },
        { timeout: 5000, maximumAge: 600000 }
      );
    }
  }, []);

  useEffect(() => {
    if (!isAvailable || !dateDebut || !dateFin) {
      setForecast([]);
      return;
    }

    const fetchWeather = async () => {
      setLoading(true);
      setError(null);

      try {
        const startStr = format(dateDebut, 'yyyy-MM-dd');
        const endStr = format(dateFin, 'yyyy-MM-dd');

        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lon}&daily=temperature_2m_max,temperature_2m_min,weather_code,precipitation_probability_max,wind_speed_10m_max&start_date=${startStr}&end_date=${endStr}&timezone=Europe/Brussels`
        );

        if (!res.ok) throw new Error('Erreur météo');

        const data = await res.json();

        const days: DailyForecast[] = data.daily.time.map(
          (date: string, i: number) => ({
            date,
            tempMin: Math.round(data.daily.temperature_2m_min[i]),
            tempMax: Math.round(data.daily.temperature_2m_max[i]),
            weatherCode: data.daily.weather_code[i],
            precipitationProbability: data.daily.precipitation_probability_max[i],
            windSpeed: Math.round(data.daily.wind_speed_10m_max[i]),
          })
        );

        setForecast(days);
      } catch (e) {
        setError('Impossible de charger la météo');
        setForecast([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, [dateDebut?.getTime(), dateFin?.getTime(), isAvailable, coords.lat, coords.lon]);

  return { forecast, loading, error, isAvailable, locationName };
};

// WMO Weather interpretation codes → emoji + label
export const getWeatherInfo = (
  code: number
): { icon: string; label: string } => {
  if (code === 0) return { icon: '☀️', label: 'Ensoleillé' };
  if (code <= 3) return { icon: '⛅', label: 'Partiellement nuageux' };
  if (code <= 48) return { icon: '🌫️', label: 'Brouillard' };
  if (code <= 57) return { icon: '🌦️', label: 'Bruine' };
  if (code <= 67) return { icon: '🌧️', label: 'Pluie' };
  if (code <= 77) return { icon: '🌨️', label: 'Neige' };
  if (code <= 82) return { icon: '🌧️', label: 'Averses' };
  if (code <= 86) return { icon: '🌨️', label: 'Averses de neige' };
  if (code >= 95) return { icon: '⛈️', label: 'Orage' };
  return { icon: '🌤️', label: 'Variable' };
};
